import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconButton, ListItemIcon, Menu, MenuItem, ToggleButton, ToggleButtonGroup, Tooltip, useMediaQuery } from '@mui/material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TimelineIcon from '@mui/icons-material/Timeline';
import CheckIcon from '@mui/icons-material/Check';
import { useDashboardStyle } from '../../context/DashboardStyleContext';
import { isTilesPageOf } from '../../utils/roleLanding';
import { DashboardStyle } from '../../interfaces/dashboardStyle.interface';

/**
 * The Tiles / Thread switch. Shown only on the dashboard (/) and only to people whose home has both styles.
 * Wide screens get a segmented control; below 600px it folds into a small menu. The pick is saved on the
 * server by the provider, so it becomes the person's default on every device.
 * Some homes' Tiles page is a separate route (Merchandising, Commercial, People, Inventory, Sales, Attendance dashboards; see
 * TILES_PAGES_BY_GROUP), so for that home's group the switch also shows there, and picking Thread on that page goes back to /.
 */

const DashboardStyleSwitch: React.FC = () => {
    const { group, style: savedStyle, ready, setStyle } = useDashboardStyle();
    const navigate = useNavigate();
    const pathname = useLocation().pathname;
    const onTilesPage = isTilesPageOf(group, pathname);
    const onDashboard = pathname === '/' || onTilesPage;
    // On a role Tiles page the page itself is the Tiles style, whatever is saved, so the toggle reads Tiles there.
    const style: DashboardStyle = onTilesPage ? 'TILES' : savedStyle;
    const narrow = useMediaQuery('(max-width:599.95px)');
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    if (group === null || !onDashboard) return null;

    const choose = (next: DashboardStyle) => {
        if (onTilesPage && next === 'THREAD') {
            // Go to / even when Thread is already saved; only write the choice when it actually changes.
            if (savedStyle !== 'THREAD') setStyle('THREAD');
            navigate('/');
            return;
        }
        if (next === style) return;
        setStyle(next);
    };

    if (narrow) {
        const pick = (next: DashboardStyle) => { setAnchor(null); choose(next); };
        return (
            <>
                <Tooltip title="Dashboard style">
                    <IconButton
                        size="small"
                        aria-label="Dashboard style"
                        aria-haspopup="menu"
                        disabled={!ready}
                        data-help="dashboard.style.menu"
                        onClick={e => setAnchor(e.currentTarget)}
                        sx={{ color: 'text.secondary' }}
                    >
                        {style === 'THREAD' ? <TimelineIcon /> : <ViewModuleIcon />}
                    </IconButton>
                </Tooltip>
                <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
                    <MenuItem selected={style === 'TILES'} onClick={() => pick('TILES')} data-help="dashboard.style.tiles">
                        <ListItemIcon>{style === 'TILES' ? <CheckIcon fontSize="small" /> : <ViewModuleIcon fontSize="small" />}</ListItemIcon>
                        Tiles
                    </MenuItem>
                    <MenuItem selected={style === 'THREAD'} onClick={() => pick('THREAD')} data-help="dashboard.style.thread">
                        <ListItemIcon>{style === 'THREAD' ? <CheckIcon fontSize="small" /> : <TimelineIcon fontSize="small" />}</ListItemIcon>
                        Thread
                    </MenuItem>
                </Menu>
            </>
        );
    }

    return (
        <ToggleButtonGroup
            exclusive
            size="small"
            value={style}
            disabled={!ready}
            aria-label="Dashboard style"
            data-help-section="dashboard.style.switch"
            onChange={(_e, next: DashboardStyle | null) => { if (next) choose(next); }}
            sx={{ mr: 1, '& .MuiToggleButton-root': { py: 0.25, px: 1.25, textTransform: 'none', fontSize: 13 } }}
        >
            <ToggleButton value="TILES" data-help="dashboard.style.tiles">Tiles</ToggleButton>
            <ToggleButton value="THREAD" data-help="dashboard.style.thread">Thread</ToggleButton>
        </ToggleButtonGroup>
    );
};

export default DashboardStyleSwitch;
